import React from "react"
import { Grid, Typography } from "@material-ui/core"
import { TextField, themeColors } from "letalkui"

import BlockBodySection from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySection"
import BlockBodySectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionEditor"
import BlockBodySectionContainer from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionContainer"

import useContentEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useContentEditor"

import { ChatBotFlowBlockContent } from "@/protocols/chatBot"

import useStyles from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/MenuListBlock/MenuListMessageDialog/styles"

const ACTION_BUTTON_MAX_LENGTH = 20
const HEADER_MAX_LENGTH = 60
const BODY_MAX_LENGTH = 1024
const FOOTER_MAX_LENGTH = 60

type MenuListMessageDialogProps = {
	chatBotFlowBlockContent: ChatBotFlowBlockContent
	onSave: (content: ChatBotFlowBlockContent) => void
	chatBotFlowBlockId?: number
}

const MenuListMessageDialog: React.FC<MenuListMessageDialogProps> = (props) => {
	const {
		chatBotFlowBlockContent,
		onSave,
		chatBotFlowBlockId
	} = props

	const classes = useStyles()
	const contentEditor = useContentEditor({ chatBotFlowBlockContent })

	const headerValue = contentEditor.content.menuListData?.header || ""
	const bodyValue = contentEditor.content.menuListData?.body || ""
	const footerValue = contentEditor.content.menuListData?.footer || ""
	const actionButtonValue = contentEditor.content.menuListData?.actionButton || ""

	const handleSave = () => {
		onSave(contentEditor.content)
	}

	const hasBodyText = Boolean(bodyValue)
	const hasPreviewContent = Boolean(headerValue || bodyValue || footerValue || actionButtonValue)

	const previewContent = (
		<Grid
			container
			direction="column"
		>
			<Typography className={classes.previewLabel}>
				Pré-visualização
			</Typography>

			<Grid
				container
				direction="column"
				className={classes.previewCanvas}
			>
				<Grid
					container
					direction="column"
					className={classes.previewBubble}
				>
					{hasPreviewContent ? (
						<>
							{headerValue && (
								<Typography className={classes.previewHeader}>
									{headerValue}
								</Typography>
							)}

							{bodyValue && (
								<Typography className={classes.previewBody}>
									{bodyValue}
								</Typography>
							)}

							{footerValue && (
								<Typography className={classes.previewFooter}>
									{footerValue}
								</Typography>
							)}

							{actionButtonValue && (
								<Typography className={classes.previewActionButton}>
									{actionButtonValue}
								</Typography>
							)}
						</>
					) : (
						<Typography className={classes.previewPlaceholder}>
							Preencha os campos abaixo para ver a prévia da mensagem
						</Typography>
					)}
				</Grid>
			</Grid>
		</Grid>
	)

	return (
		<BlockBodySection
			fullWidth
		>
			<BlockBodySectionEditor
				title="MENSAGEM DO MENU"
				onSave={handleSave}
				onOpen={contentEditor.reset}
				AsideDrawerProps={{
					width: "510px",
					children: (
						<Grid
							container
							direction="column"
							spacing={2}
						>
							<Grid item>
								{previewContent}
							</Grid>

							<Grid item>
								<TextField
									type="text"
									label="Cabeçalho"
									placeholder="Cabeçalho (opcional)"
									value={headerValue}
									onChange={(event) => contentEditor.changeContent({ menuListData: { header: event.target.value.slice(0, HEADER_MAX_LENGTH) } })}
									fullWidth
									inputProps={{ maxLength: HEADER_MAX_LENGTH }}
								/>
							</Grid>

							<Grid item>
								<TextField
									type="text"
									label="Mensagem"
									placeholder="Mensagem"
									value={bodyValue}
									onChange={(event) => contentEditor.changeContent({ menuListData: { body: event.target.value.slice(0, BODY_MAX_LENGTH) } })}
									fullWidth
									multiline
									minRows={3}
									inputProps={{ maxLength: BODY_MAX_LENGTH }}
								/>

								<Typography className={classes.characterCounter}>
									{bodyValue.length}/{BODY_MAX_LENGTH}
								</Typography>
							</Grid>

							<Grid item>
								<TextField
									type="text"
									label="Rodapé"
									placeholder="Rodapé (opcional)"
									value={footerValue}
									onChange={(event) => contentEditor.changeContent({ menuListData: { footer: event.target.value.slice(0, FOOTER_MAX_LENGTH) } })}
									fullWidth
									inputProps={{ maxLength: FOOTER_MAX_LENGTH }}
								/>
							</Grid>

							<Grid item>
								<TextField
									type="text"
									label="Texto do botão"
									placeholder="Texto do botão"
									value={actionButtonValue}
									onChange={(event) => contentEditor.changeContent({ menuListData: { actionButton: event.target.value.slice(0, ACTION_BUTTON_MAX_LENGTH) } })}
									fullWidth
									inputProps={{ maxLength: ACTION_BUTTON_MAX_LENGTH }}
								/>
								<Typography className={classes.characterCounter}>
									{actionButtonValue.length}/{ACTION_BUTTON_MAX_LENGTH}
								</Typography>
							</Grid>
						</Grid>
					)
				}}
			>
				<BlockBodySectionContainer
					text={hasBodyText ? bodyValue : ""}
					placeholder="Nenhuma mensagem adicionada"
					backgroundColor={themeColors.colors.system.green[50]}
					chatBotFlowBlockId={chatBotFlowBlockId}
					errorType="block_missing_content"
				/>
			</BlockBodySectionEditor>
		</BlockBodySection>
	)
}

export default MenuListMessageDialog
